import { cn } from "@/lib/utils";

/** Returns a Tailwind stroke class for a coverage score (green >= 80, orange >= 50, red < 50). */
export function getCoverageScoreClassName(score: number): string {
  if (score >= 80) return "stroke-green-500 dark:stroke-green-400";
  if (score >= 50) return "stroke-orange-500 dark:stroke-orange-400";
  return "stroke-red-500 dark:stroke-red-400";
}

interface CircularProgressProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  strokePlaceholderClassName?: string;
  strokeClassName?: string;
  indeterminate?: boolean;
  showValue?: boolean;
  dashed?: boolean;
}

export function CircularProgress({
  value,
  size = 80,
  strokeWidth = 8,
  strokePlaceholderClassName = "stroke-gray-300 dark:stroke-gray-600",
  strokeClassName = "stroke-green-500 dark:stroke-green-400",
  indeterminate = false,
  showValue = true,
  dashed = false,
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashSegment = circumference / 12;
  const offset = indeterminate
    ? circumference * 0.85
    : circumference - (value / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className={indeterminate ? "animate-spin" : "-rotate-90"}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={dashed ? `${dashSegment} ${dashSegment}` : undefined}
          className={strokePlaceholderClassName}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={cn(
            strokeClassName,
            !indeterminate && "transition-all duration-1000 ease-in-out",
          )}
        />
      </svg>
      {!indeterminate && showValue && (
        <span
          className="flex absolute inset-0 justify-center items-center font-semibold text-gray-800 dark:text-gray-200"
          style={{ fontSize: size * 0.25 }}
        >
          {value}%
        </span>
      )}
    </div>
  );
}
